import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, Stack, Typography, ToggleButton, ToggleButtonGroup, Button, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress } from '@mui/material';
import { useSelector, dispatch } from 'src/redux/store';
import { selectLeads } from 'src/redux/slices/lead';
import { callApi } from 'src/utils/apiCall';
import { getSelectedLeadIdsForApi } from 'src/utils/selectionModel';
import { useSnackbar } from 'notistack';
import { Transition } from '.';
import Iconify from '../Iconify';

const BulkCommunicationPopup = ({ open, onClose, onCompleted, entityLabel = 'lead' }) => {
  const { multiLead } = useSelector((state) => state.lead);
  const { practiceId } = useSelector((state) => state.practiceTreatmentList);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [value, setValue] = React.useState('Yes');
  const [submitting, setSubmitting] = React.useState(false);
  const leadIds = getSelectedLeadIdsForApi(multiLead);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      ids: leadIds,
      value: value === 'Yes' ? 'true' : 'false',
      field: 'is_open_for_communication',
    };
    const key = enqueueSnackbar('Updating communication status…', { variant: 'info', persist: true });
    // Close modal immediately after firing API for a snappier UX
    if (onClose) onClose();
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/leads/bulk_update/?is_archived_included=true`, 'post', payload);
      closeSnackbar(key);
      const updatedCount = leadIds.length;
      if (res?.status === 200) {
        enqueueSnackbar(`Updated ${updatedCount} ${entityLabel}${updatedCount !== 1 ? 's' : ''}`, { variant: 'success' });
        // clear selection
        dispatch(selectLeads({ leadId: [], type: '', loading: false }));
        if (onCompleted) onCompleted();
      } else {
        enqueueSnackbar('Failed to update communication status', { variant: 'error' });
      }
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar('Failed to update communication status', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = leadIds.length;
  const yesSelected = value === 'Yes';
  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth='sm' fullWidth>
      <DialogTitle>Change communication</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity='info' icon={<Iconify icon='eva:info-fill' />}>Applies to {selectedCount} selected {entityLabel}{selectedCount !== 1 ? 's' : ''}.</Alert>
          <Typography variant='body2'>Open to communication</Typography>
          <ToggleButtonGroup
            color='primary'
            value={value}
            exclusive
            onChange={(_, v) => { if (v) setValue(v); }}
            aria-label='open-communication'
            sx={{
              '& .MuiToggleButton-root': { px: 3, py: 1.2 },
              '& .Mui-selected': { bgcolor: yesSelected ? 'success.lighter' : 'error.lighter' },
            }}
          >
            <ToggleButton value='Yes'>
              <Iconify icon='eva:checkmark-circle-2-fill' style={{ marginRight: 8 }} /> Yes
            </ToggleButton>
            <ToggleButton value='No'>
              <Iconify icon='eva:close-circle-fill' style={{ marginRight: 8 }} /> No
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant='caption' color='text.secondary'>
            This updates the “Open to communication” field on all selected leads. It will not restore or archive records.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={submitting || !(selectedCount > 0)} startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : <Iconify icon='eva:save-fill' />}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkCommunicationPopup.propTypes = {
  entityLabel: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCompleted: PropTypes.func,
};

export default BulkCommunicationPopup;
